// Showing the Science in Practice. A live clinic from the San Diego coaches
// retreat on how skill acquisition actually looks on the court.
// Mux: HL7E6IbMKedprWqdYc623iVe63KQXpzUEpC7naSIZTc (1:13:04)
//
// The cards are BAM's editorial summary of the clinic, not a transcript.
// Only text inside quotation marks is Coleman's own words, and every
// quote carries the time he says it. talkQuotes.test.js checks each one
// against knowledge-base/transcripts/masterclass/
// showing-the-science-in-practice.md (the clinic's captions), so a quote
// that isn't in the talk can't ship. Summary copy stays in the third
// person so it can't read as his voice. No em dashes.

export const SCIENCE_IN_PRACTICE_MODULE_KEY = "science-in-practice";

const VIDEO = "HL7E6IbMKedprWqdYc623iVe63KQXpzUEpC7naSIZTc";

export const SCIENCE_IN_PRACTICE_MODULE = {
  key: SCIENCE_IN_PRACTICE_MODULE_KEY,
  title: "Showing the Science in Practice",
  sub: "A live clinic on how skill acquisition actually looks on the court, not the way it reads in a textbook.",
  coverVideoId: VIDEO,
  duration: "1h 13min clinic plus 10 lessons",
  chapters: [
    {
      id: "watch",
      title: "Watch the clinic",
      blocks: [
        { kind: "text", md:
          "Filmed live at the retreat. It walks through the core skill-acquisition ideas the way they actually look on the court. Watch it straight through. The cards below follow the spine of it, in order." },
        { kind: "text", md:
          "**About these cards.** They're BAM's summary of the clinic, written from the recording. They're not a transcript. Only the lines in quotation marks are Coleman's own words, each with the time he says it in the video." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Showing the Science in Practice. Full session." },
      ],
    },
    {
      id: "intentions",
      title: "1. Your intentions do not matter.",
      blocks: [
        { kind: "text", md:
          "\"Your intentions as a coach do not matter.\" (3:14)" },
        { kind: "text", md:
          "The nervous system learns what the athlete actually does, not what the coach intends. A coach in the room gave the classic example: set up a logistically complex drill and players spend their attention figuring out the drill. Their nervous system may never get to the skill." },
        { kind: "text", md:
          "\"Whatever is on your practice plan does not matter. It does not matter if the athlete is not experiencing what they need to experience to learn.\" (5:27)" },
        { kind: "text", md:
          "**Three questions to ask about any drill, game, or method:**\n\n- Are your intentions and their actual learning experience aligned?\n- Does what they are learning transfer to the game?\n- Are they learning it in a way that keeps them coming back?" },
        { kind: "text", md:
          "If you cannot answer yes to all three, take a step back and look in the mirror at what you set up. It is fine to switch it up and adjust until all three are satisfied." },
      ],
    },
    {
      id: "study-of-transfer",
      title: "2. It is all the study of transfer.",
      blocks: [
        { kind: "text", md:
          "\"Does what we set up and practice actually show up in the game? That's it. That's literally the only question that all this research, all these methods are trying to answer.\" (8:05)" },
        { kind: "text", md:
          "That is the whole field of skill acquisition. The ironic part is that something does not need to look exactly like the game to transfer." },
        { kind: "text", md:
          "Coleman's read from working with coaches around the world: coaches understand the what. You watch the game, see a player do something, then have them do it in practice. The disconnect is the how. How the athlete executes that skill in a training environment is what actually breeds transfer." },
      ],
    },
    {
      id: "transfer-spectrum",
      title: "3. General transfer and specific transfer.",
      blocks: [
        { kind: "text", md:
          "**Game-like versus not-game-like is the wrong argument. Both transfer. You just use each side of the spectrum on purpose.**" },
        { kind: "text", md:
          "**Specific transfer** is one end. Five on five, eight-minute quarters, refs, a crowd, the real situations a player will face. It looks like the game, and you key in on making it look as close to the game as possible." },
        { kind: "text", md:
          "**General transfer** is the other end. Building qualities that do not look like the game but still help it. Soccer, football, the weight room, the warmup you just ran. You will never back squat in a game, but you know how it helps." },
        { kind: "text", md:
          "So stop arguing that something looks too much like a game or not enough like a game. The skill is knowing which side of the toolbox to reach for. General is where you get creative. Specific is where you key in." },
      ],
    },
    {
      id: "situations-decisions-skills",
      title: "4. Situations, decisions, skills.",
      blocks: [
        { kind: "text", md:
          "**Build the plan from the top down. Situations first, then decisions, then the skills to execute, then the qualities behind those skills.**" },
        { kind: "text", md:
          "**Situations.** What is this player actually going to face. A spray-out closeout in the corner. A specific ball-screen coverage your offense runs into. Get as specific as you can." },
        { kind: "text", md:
          "**Decisions.** Inside that situation. Off a drag screen that might be the over or under read, then the hedge read, then the low-man tag read. Often two or three decisions live inside one situation." },
        { kind: "text", md:
          "**Skills.** What the athlete needs to execute to take advantage. The right pass, the ball control to change direction, the pull-up." },
        { kind: "text", md:
          "Then the qualities behind those skills loop you right back to general transfer. When you have no bodies to make decisions or build situations, you identify the skills and the qualities behind them and work general. The two always feed each other." },
      ],
    },
    {
      id: "start-with-the-whole",
      title: "5. Start with the whole.",
      blocks: [
        { kind: "text", md:
          "**Start with the full thing, let them struggle, then break out what is actually broken.**" },
        { kind: "text", md:
          "Coleman used to start basic and build up until the group arrived at a 3v3 they were decent at. \"I've completely shifted my mindset on that now. We start with the full thing because it allows us to truly analyze every layer of it.\" (41:58)" },
        { kind: "text", md:
          "Start with the whole 3v3. Let them be bad at it for five minutes. Now you can actually see the rate limiting factor. Maybe it is handling pressure. Maybe it is the short-roll read. Break out that one thing, target it, then go back to the whole. Otherwise you might spend twenty minutes on something they would have figured out anyway." },
        { kind: "text", md:
          "He's clear that building up from the basics also works. His reason for starting with the whole is efficiency: \"I've just found that one optimizes the efficiency of our sessions a little bit more.\" (52:36)" },
        { kind: "text", md:
          "Two more things. \"If your players can learn without you saying a word, you have done a way better job than if you did say a word because now they're autonomously learning these things.\" (42:31) And overestimate the athlete. Coaches tend to underestimate players by starting basic and building up." },
      ],
    },
    {
      id: "constraint-not-cue",
      title: "6. Set a constraint instead of giving a cue.",
      blocks: [
        { kind: "text", md:
          "**Do not say make a quick decision. Build a problem where they have to make one.**" },
        { kind: "text", md:
          "You can repeat quicker decision, quicker decision all day. Or you can set a one-hand catch on the short roll so they either decide fast or lose the ball. \"So that's my way to say quick decision, but I don't say quick decision.\" (46:37)" },
        { kind: "text", md:
          "That is the difference between hoping your words transfer to the game and creating a problem the player has to solve. They satisfy the rules of the game or they do not, and the behavior you want has room to emerge." },
      ],
    },
    {
      id: "cla-is-a-lens",
      title: "7. The constraints-led approach is a lens, not a drill type.",
      blocks: [
        { kind: "text", md:
          "**It is not just small-sided games and crazy drills. It is how you see the learning environment.**" },
        { kind: "text", md:
          "It can be on air. Put a hand up so they have to shoot over it, and you are asking for a higher release angle without ever saying it: \"instead of saying shoot higher. I create a problem for them to solve.\" (1:10:12)" },
        { kind: "text", md:
          "Constraints limit possibilities, which forces adaptability and creativity. That is true for players, and it is true for you. Some of the best new games and constraints come from being stuck. Two players and a passing problem forces you to invent, the same way it forces the player to." },
        { kind: "text", md:
          "Even pure block shooting can fit under it. If a player struggles mentally without a high success rate, feeding them perfect passes at a high make rate is a constraint built around their individual needs. Coleman still argues block shooting should typically be used in specific situations. The point is that the approach is not a drill. It is how you see and shape the environment over time." },
      ],
    },
    {
      id: "affordances",
      title: "8. Affordances. Why players do not make the read.",
      blocks: [
        { kind: "text", md:
          "**An affordance is an opportunity for action, and every player sees different ones because their individual constraints are different.**" },
        { kind: "text", md:
          "Two players with the same surface game will see different reads. One dumps it off on the big, one pulls it back out. Neither is right or wrong. They simply see different affordances." },
        { kind: "text", md:
          "So when a player does not make a read, stop asking them why. Ask yourself why. Maybe they do not trust the teammate they are passing to. Maybe their right knee hurts and you would never know. Maybe their coordination patterns are different. Maybe that read is a good general solution but not the one for them." },
        { kind: "text", md:
          "The lens of affordances is how you stop coaching in a vacuum where X is the right decision and Y is the wrong one, and start understanding how each athlete actually interacts with the game." },
      ],
    },
    {
      id: "exaggerate-incentivize-slow",
      title: "9. Exaggerate, incentivize, slow it down.",
      blocks: [
        { kind: "text", md:
          "**Three ways to help a player see and use an affordance they keep missing.**" },
        { kind: "text", md:
          "**Exaggerate it.** Hands behind the back. A help defender who cannot move until the ball is passed. A baseline-touch rule that opens up the early advantage in transition. Make the read easier to see, then scale it back toward normal." },
        { kind: "text", md:
          "**Incentivize it.** Points. The pocket pass is worth two. Going off two feet is worth three. The 45 cut is worth two. Competitive players start seeing it fast." },
        { kind: "text", md:
          "**Slow it down.** For the slower IQ-based reads, play at half speed so the player can actually process it. Pick the speed back up later." },
        { kind: "text", md:
          "One catch on the points. You need a culture that wants to win badly enough to chase the system, but not so badly that they cheat it. They should respect the intention of the constraint even when it costs them." },
      ],
    },
    {
      id: "action-capacity",
      title: "10. Sometimes it is action capacity, not perception.",
      blocks: [
        { kind: "text", md:
          "**A player can see the affordance and still not be able to act on it.**" },
        { kind: "text", md:
          "A coach in the room offered one example: a seventh grader who sees the skip pass but is not strong enough to throw it. Another: a young guard who would be great at finding space to the left but cannot really go left yet." },
        { kind: "text", md:
          "That is when on-air work earns its keep. Build the strength, the movement pattern, the footwork, so the ability to act exists. Then put it back into the game, so that more affordances become attractive to them because they can finally act on them." },
      ],
    },
  ],
};
